import { useState, useCallback, useMemo, useEffect } from 'react';

interface UsePaginationProps {
  initialPage?: number;
  initialLimit?: number;
  initialTotal?: number;
}

export function useChannelPagination({
  initialPage = 1,
  initialLimit = 10,
  initialTotal = 0,
}: UsePaginationProps) {
  const [currentPage, setCurrentPage] = useState(initialPage);
  const [limit] = useState(initialLimit);
  const [total, setTotal] = useState(initialTotal);

  const totalPages = useMemo(() => Math.ceil(total / limit), [total, limit]);

  useEffect(() => {
    if (totalPages === 0) {
      return;
    }

    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const goToPage = useCallback(
    (page: number) => {
      if (page >= 1 && page <= totalPages) {
        setCurrentPage(page);
      }
    },
    [totalPages]
  );

  const handleNextPage = useCallback(() => {
    if (currentPage < totalPages) {
      setCurrentPage((prev) => prev + 1);
    }
  }, [currentPage, totalPages]);

  const handlePrevPage = useCallback(() => {
    if (currentPage > 1) {
      setCurrentPage((prev) => prev - 1);
    }
  }, [currentPage]);

  const resetPagination = useCallback(() => {
    setCurrentPage(1);
  }, []);

  return {
    total,
    setTotal,
    currentPage,
    limit,
    totalPages,
    goToPage,
    handleNextPage,
    handlePrevPage,
    resetPagination,
  };
}

